import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import _ from 'lodash'
import {
  UserDataType,
  WarehouseType,
  TenantType,
} from 'utils/types'
import { CheckoutAdditionalField } from '../checkoutAdditionalFields/types'
import * as S from './style'
import {
  openError,
  openToast,
  fetchCustomBerthLabel,
} from 'containers/App/reducers'

import {
  changeWarehouse,
  changeTenant,
  fetchReceptionParameters,
} from '../reducers'
import FinishToast from '../components/FinishToast'
import CanNotUse from '../components/CanNotUse'
import { canUpdateReceptionCustom } from '../ReceptionCustomService'
import Header from '../components/Header'
import {
  warehouseIdSelectors,
  tenantIdSelectors,
  warehousesSelectors,
  tenantsSelectors,
} from '../selectors'
import { userDataSelectors } from 'containers/App/selectors'
import { TAB_MODE } from '../constants'
import CheckoutAdditionalFieldEditor from '../checkoutAdditionalFields/components/CheckoutAdditionalFieldEditor'
import CheckoutAdditionalFieldsForm from '../checkoutAdditionalFields/components/CheckoutAdditionalFieldsForm'
import UnsavedChangesDialog from '../checkoutAdditionalFields/components/UnsavedChangesDialog'
import { useReceptionCustomHeader } from '../hooks/useReceptionCustomHeader'
import { useCreateCheckoutAdditionalFields } from '../checkoutAdditionalFields/hooks/useCreateCheckoutAdditionalFields'
import { useFetchCheckoutAdditionalFields } from '../checkoutAdditionalFields/hooks/useFetchCheckoutAdditionalFields'
import { useCheckoutAdditionalFieldsForm } from '../checkoutAdditionalFields/hooks/useCheckoutAdditionalFieldsForm'
import {
  UnsavedChangesProvider,
  useRegisterUnsavedChanges,
  UNSAVED_CHANGES_ID,
} from '../checkoutAdditionalFields/components/UnsavedChangesContext'

type FetchCustomBerthLabelParams = {
  tenantId?: number
  hasTenant?: boolean
}

type HasIdAndName = {
  id: string | number;
  name: string;
};

type Props = {
  warehouseId: number
  tenantId: number
  warehouses: WarehouseType[]
  tenants: TenantType[]
  userData: UserDataType
  fetchReceptionParameters: (param: string) => void
  changeWarehouse: (warehouseId: number) => void
  changeTenant: (tenantId: number) => void
  fetchCustomBerthLabel: (params?: FetchCustomBerthLabelParams) => void
  openError: (message: string, useNetworkError?: boolean) => void
  openToast: (toastContent: React.ReactNode) => void
}

const getOptions = <T extends HasIdAndName>(arr: T[]) =>
  arr.map((item) => ({
    value: item.id,
    label: item.name,
  }));

const CheckoutAdditionalFieldsPageContent: React.FC<Props> = ({
  warehouseId,
  tenantId,
  warehouses,
  tenants,
  userData,
  fetchReceptionParameters,
  changeTenant,
  changeWarehouse,
  fetchCustomBerthLabel,
  openError,
  openToast,
}) => {
  const [isOpenEditor, setIsOpenEditor] = useState(false)
  const [selectedFieldIndex, setSelectedFieldIndex] = useState<number | null>(null)

  const warehouseOptions = useMemo(() => getOptions(warehouses), [warehouses])
  const tenantOptions = useMemo(() => getOptions(tenants), [tenants])
  const selectedTenant = tenants.find((t) => t.id === tenantId)
  const canUpdate = canUpdateReceptionCustom(userData?.attributes?.is_warehouse_admin, selectedTenant || {})

  const { submit: submitCheckoutAdditionalFields } = useCreateCheckoutAdditionalFields(
    tenantId,
    {
      onSuccess: async () => {
        await fetchData(tenantId)
        openToast(FinishToast)
      },
      onError: (error) => {
        openError(error.message, false)
      },
    }
  )

  const { data: checkoutAdditionalFields, fetchData } = useFetchCheckoutAdditionalFields({
    onSuccess: (data) => {
      setFormData(_.cloneDeep(data))
    },
    onError: () => {
      openError('データの取得に失敗しました', false)
    },
  })

  const {
    formData,
    setFormData,
    addField,
    updateField,
    handleSubmit,
    isDirty,
  } = useCheckoutAdditionalFieldsForm({
    defaultFormData: checkoutAdditionalFields || [],
    onSubmit: async (fields) => {
      await submitCheckoutAdditionalFields(fields)
    },
    onValidationError: (message) => {
      openError(message, false)
    },

  })

  // 親フォームの isDirty を登録
  useRegisterUnsavedChanges({ isDirty, id: UNSAVED_CHANGES_ID.FORM })

  const handleOpenCheckoutAdditionalFieldsEditor = useCallback((index: number) => {
    setSelectedFieldIndex(index)
    setIsOpenEditor(true)
  }, [])

  const handleCloseCheckoutAdditionalFieldsEditor = useCallback(() => {
    setIsOpenEditor(false)
    setSelectedFieldIndex(null)
  }, [])

  const handleUpdateCheckoutAdditionalFieldsForm = useCallback((updatedField: CheckoutAdditionalField) => {
    if (selectedFieldIndex === null) return
    updateField(selectedFieldIndex, updatedField)
    handleCloseCheckoutAdditionalFieldsEditor()
  }, [selectedFieldIndex, updateField, handleCloseCheckoutAdditionalFieldsEditor])

  const { onChangeWarehouse, onChangeTenant } = useReceptionCustomHeader({
    warehouses,
    changeWarehouse,
    changeTenant,
    fetchReceptionParameters,
    fetchCustomBerthLabel,
    tabMode: TAB_MODE.CHECKOUT_ADDITIONAL_FIELDS,
  })

  useEffect(() => {
    fetchReceptionParameters(TAB_MODE.CHECKOUT_ADDITIONAL_FIELDS)
  }, [fetchReceptionParameters])

  useEffect(() => {
    fetchData(tenantId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId])

  const selectedField = useMemo(() => {
    return selectedFieldIndex !== null ? formData[selectedFieldIndex] : undefined
  }, [formData, selectedFieldIndex])

  const selectedWarehouseOption = useMemo(
    () => warehouseOptions.find((warehouse) => warehouse.value === warehouseId),
    [warehouseOptions, warehouseId]
  )

  const selectedTenantOption = useMemo(
    () => tenantOptions.find((t) => t.value === tenantId),
    [tenantOptions, tenantId]
  )

  return (
    <div>
      <Header
        warehouses={warehouseOptions}
        tenants={tenantOptions}
        selectedWarehouse={selectedWarehouseOption}
        selectedTenant={selectedTenantOption}
        onChangeWarehouse={onChangeWarehouse}
        onChangeTenant={onChangeTenant}
      />
      {!selectedTenant?.canCustomizeReception ? (
        <CanNotUse />
      ) : isOpenEditor && selectedField ? (
        <S.TimeSettingWrapper>
          <S.SettingContent>
            <CheckoutAdditionalFieldEditor
              selectedField={selectedField}
              onClose={handleCloseCheckoutAdditionalFieldsEditor}
              onSave={handleUpdateCheckoutAdditionalFieldsForm}
              canUpdate={canUpdate}
              tenantId={tenantId}
              tenants={tenants}
              userData={userData}
              openError={openError}
            />
          </S.SettingContent>
        </S.TimeSettingWrapper>
      ) : (
        <CheckoutAdditionalFieldsForm
          fields={formData}
          originalFields={checkoutAdditionalFields || []}
          onUpdateFields={setFormData}
          onAddField={addField}
          onSubmit={handleSubmit}
          onClickEdit={handleOpenCheckoutAdditionalFieldsEditor}
          canUpdate={canUpdate}
        />
      )}
      <UnsavedChangesDialog />
    </div>
  )
}

const CheckoutAdditionalFieldsPage: React.FC<Props> = (props) => {
  return (
    <UnsavedChangesProvider>
      <CheckoutAdditionalFieldsPageContent {...props} />
    </UnsavedChangesProvider>
  )
}

const mapStateToProps = createStructuredSelector({
  warehouseId: warehouseIdSelectors(),
  tenantId: tenantIdSelectors(),
  warehouses: warehousesSelectors(),
  tenants: tenantsSelectors(),
  userData: userDataSelectors(),
})

const mapDispatchToProps = {
  changeWarehouse,
  openToast,
  changeTenant,
  fetchReceptionParameters,
  fetchCustomBerthLabel,
  openError,
}

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutAdditionalFieldsPage)
